import { ReadonlyVec3, vec3 } from 'gl-matrix';

/** One pass of `p_i ← p_i + weight · (midpoint(p_{i−1}, p_{i+1}) − p_i)` over a closed polygon. */
function laplacianPass(points: vec3[], weight: number): vec3[] {
    const n = points.length;
    return points.map((p, i) => {
        const prev = points[(i + n - 1) % n];
        const next = points[(i + 1) % n];
        return vec3.fromValues(
            p[0] + weight * (0.5 * (prev[0] + next[0]) - p[0]),
            p[1] + weight * (0.5 * (prev[1] + next[1]) - p[1]),
            p[2] + weight * (0.5 * (prev[2] + next[2]) - p[2])
        );
    });
}

/**
 * Taubin λ|μ smoothing (λ 0.5, μ −0.5) of a closed polygon in mm, without a
 * repeated closing vertex, by `iterations` pass pairs. No mode is amplified
 * and a planar polygon stays in its plane, but a coarse ring shrinks, so
 * callers resample densely first. Returns a new single-precision polygon of
 * the same length; fewer than 3 vertices or `iterations` ≤ 0 return copies.
 */
export function taubinSmoothClosed(
    polygonMm: readonly ReadonlyVec3[],
    iterations: number
): vec3[] {
    let points = polygonMm.map((p) => vec3.clone(p as vec3));
    if (points.length < 3 || iterations <= 0) {
        return points;
    }
    for (let i = 0; i < iterations; i++) {
        points = laplacianPass(laplacianPass(points, 0.5), -0.5);
    }
    return points;
}
